import React from 'react';
import { Lock } from 'lucide-react';

interface CaseBlockedBannerProps {
  /** The rule message to display (e.g., "Companies with duplicate EINs cannot be processed") */
  ruleMessage: string;
  /** When the case was blocked (ISO timestamp) */
  blockedAt?: string;
}

/**
 * Banner displayed when a case is blocked due to business rule violation.
 *
 * Matches the design shown in the product screenshots:
 * - Pink/red background
 * - Lock icon
 * - "Case Blocked - Does Not Meet Processing Rules" title
 * - Description text explaining the block
 * - Rule: [specific rule message]
 */
const CaseBlockedBanner: React.FC<CaseBlockedBannerProps> = ({
  ruleMessage,
  blockedAt,
}) => {
  // Format the blocked date if provided
  const formattedDate = blockedAt
    ? new Date(blockedAt).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div className="flex items-start space-x-3">
        {/* Lock Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
          <Lock className="w-5 h-5 text-red-600" />
        </div>

        <div className="flex-1">
          {/* Title */}
          <h3 className="text-lg font-semibold text-red-800">
            Case Blocked - Does Not Meet Processing Rules
          </h3>

          {/* Description */}
          <p className="text-sm text-red-700 mt-1">
            This case has been blocked because it does not meet business processing
            rules and cannot be processed through the Payroll Collection Agent.
          </p>

          {/* Rule Message */}
          <p className="text-sm text-red-700 mt-2">
            <span className="font-semibold">Rule:</span> {ruleMessage}
          </p>

          {/* Blocked timestamp (if provided) */}
          {formattedDate && (
            <p className="text-xs text-red-500 mt-2">Blocked on {formattedDate}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseBlockedBanner;
